/**
 * Notification Engine — one tabbed screen, the same shape as Notifications.
 *
 * The engine's screens were eight separate routes in a sidebar group while the
 * live system's equivalents sat as tabs under one header. Same job, two
 * different shapes, and the engine's was the one that made you leave the screen
 * to answer a question about the thing you were configuring.
 *
 * Ordered by audience, as the live group is: what a person does with their own
 * notifications first, then the configuration, then the tools.
 */
import GroupScreen from '../../components/layout/group-screen';
import SubTabbedPanel from '../../components/layout/sub-tabbed-panel';
import { JOB_NAMES } from '../../constants/jobNames';
import EngineInbox from './engine-inbox';
import NotificationPreferences from '../notification-preferences/notification-preferences';
import NotificationMasterList from '../notification-masters/notification-master-list';
import NotificationTypeList from '../notification-types/notification-type-list';
import RecipientMasterList from '../recipient-masters/recipient-master-list';
import RecipientListList from '../recipient-lists/recipient-list-list';
import ActionMasterList from '../action-masters/action-master-list';
import NotificationSchedulesTab from '../schedules/notification-schedules-tab';

const NotificationEngineGroup = () => (
  <GroupScreen
    icon="bell-ring"
    iconBgColor="bg-indigo-600"
    title="Notification Engine"
    subtitle="What gets sent, to whom, when — and what each fire did"
    testId="SCR-NotificationEngineGroup"
    tabs={[
      // A person's own notifications first. No job gate: receiving a
      // notification is not a configuration permission, and gating the inbox
      // would hide it from everyone who only receives them.
      { key: 'inbox', header: 'Inbox', leftIcon: 'inbox', jobs: [], element: <EngineInbox /> },
      { key: 'preferences', header: 'Settings', leftIcon: 'settings-2', jobs: [], element: <NotificationPreferences /> },

      // Then the configuration, in the order it is built. (The record of what
      // actually fired lives on the Transactional Dashboard.)
      //
      // Types and Master share a tab because they are one job split in half: a
      // master REQUIRES a type, and the type owns the channels and delivery
      // policy every master inherits. Recipients and Lists likewise — a list is
      // a group of recipients. The lists themselves are untouched; only the way
      // they are reached is grouped, which is what brought eight tabs wrapping
      // onto two rows back to one.
      {
        key: 'notifications', header: 'Notifications', leftIcon: 'bell',
        jobs: [JOB_NAMES.NOTIFICATION_TYPE, JOB_NAMES.NOTIFICATION_MASTER],
        element: (
          <SubTabbedPanel
            testId="NotificationEngine.Notifications"
            items={[
              // Master first: it is the thing people come here to edit. The type
              // is set up once and revisited rarely, so it reads as the second
              // step even though a master depends on it.
              { key: 'master', label: 'Notification Master', icon: 'bell',
                jobs: [JOB_NAMES.NOTIFICATION_MASTER], element: <NotificationMasterList embedded /> },
              { key: 'types', label: 'Notification Types', icon: 'tags',
                jobs: [JOB_NAMES.NOTIFICATION_TYPE], element: <NotificationTypeList embedded /> },
            ]}
          />
        ),
      },
      {
        key: 'recipients', header: 'Recipients', leftIcon: 'contact',
        jobs: [JOB_NAMES.RECIPIENT_MASTER, JOB_NAMES.RECIPIENT_LIST],
        element: (
          <SubTabbedPanel
            testId="NotificationEngine.Recipients"
            items={[
              { key: 'people', label: 'Recipient Master', icon: 'contact',
                jobs: [JOB_NAMES.RECIPIENT_MASTER], element: <RecipientMasterList embedded /> },
              { key: 'lists', label: 'Recipient Lists', icon: 'book-user',
                jobs: [JOB_NAMES.RECIPIENT_LIST], element: <RecipientListList embedded /> },
            ]}
          />
        ),
      },
      { key: 'actions', header: 'Actions', leftIcon: 'send', jobs: [JOB_NAMES.ACTION_MASTER], element: <ActionMasterList embedded /> },
      { key: 'schedules', header: 'Schedules', leftIcon: 'calendar-clock', jobs: [JOB_NAMES.SCHEDULE_MASTER], element: <NotificationSchedulesTab embedded /> },
    ]}
  />
);

export default NotificationEngineGroup;
